import {isSelectiveAiDomains} from 'common/HelperFunctions'

import {
  REDUX_GET_ATTENDANCE_SETTING,
  REDUX_UPDATE_ATTENDANCE_SETTING,
} from '../Actions/Attendance/ActionTypes/ReduxAtionTypes'
import {
  REDUX_ADD_BOT_MESSAGE,
  REDUX_ADD_CONVERSATION_HISTORY,
  REDUX_ADD_USER_MESSAGE,
  REDUX_CLEAR_CHAT,
  REDUX_RESET_MENU_STATE,
  REDUX_RESTORE_CHAT_SESSION,
  REDUX_SET_MENU_STATE,
  REDUX_SET_MESSAGE_UNDERSTOOD_AS,
  REDUX_SET_SELECTED_MENU_PATH,
  REDUX_SET_TYPING,
  REDUX_UPDATE_MESSAGE_STATUS,
} from '../Actions/Chatbot/ActionTypes/ReduxActionTypes'
import {
  REDUX_GENERAL_SETTINGS,
  REDUX_GET_CLASSES,
  REDUX_GET_GRADES,
  REDUX_GET_SECTIONS,
  REDUX_GET_STUDENTS,
  REDUX_GET_TEACHERS,
  REDUX_SAVE_SUBJECTS,
  REDUX_SELECT_LANGUAGE,
  REDUX_SET_BRANCHES,
  REDUX_SET_CUSTOM_TITLES,
  REDUX_SET_HEADER_DROPDOWN,
  REDUX_STATE_LOADING,
  REDUX_TENANT_SESSIONS,
  REDUX_USER_PREFERENCES,
} from '../Actions/General/ActionTypes/ReduxActionTypes'
import {REDUX_LEVELS} from '../Actions/Level/ActionTypes/ReduxActionTypes'
import {REDUX_DESTROY_SESSION} from '../Actions/SignIn/ActionTypes/ReduxActionTypes'
import {REDUX_GET_USER_SUBJECTS} from '../Actions/Subject/ActionTypes/ReduxActionTypes'
import {
  REDUX_GET_ALL_CONSTRAINTS,
  REDUX_GET_TIMETABLE,
  REDUX_GET_TIMETABLE_CATEGORIES,
  REDUX_GET_TIMETABLE_LIST_DATA,
  REDUX_GET_TIMETABLE_SUBCATEGORY_BY_ID,
  REDUX_SET_SELECTED_BRANCH_ID,
  REDUX_SET_SELECTED_DURATION_ID,
  REDUX_SET_SELECTED_LEVEL_ID,
  REDUX_SET_SELECTED_VERSION_ID,
} from '../Actions/Timetable/ActionTypes/ReduxActionTypes'

export type MainReducer = {
  selectedLanguage: string
  isFetching: {[key: string]: any}
  subjects: {[key: string]: any}[]
  headerDropdown: boolean
  generalSettings: {[key: string]: any}
  /** True once the server has answered; before that an empty generalSettings means "not asked yet". */
  generalSettingsLoaded: boolean
  userPreferences: {[key: string]: any} | null
  branches: {[key: string]: any}[]
  userBranches: {[key: string]: any}[]
  custom_titles: {[key: string]: any}[]
  tenantSessions: {[key: string]: any}
  classes: {[key: string]: any}[]
  grades: {[key: string]: any}[]
  teachers: {[key: string]: any}[]
  students: {[key: string]: any}[]
  sections: {[key: string]: any}[]
  timetable_list_stats: Record<string, number> | null
  attendance_setting: {[key: string]: any}[]
  get_user_subjects: {[key: string]: any}[]
  levels: {[key: string]: any}[]
  get_timetable_categories: {[key: string]: any}[]
  get_timetable_subcategories: {[key: string]: any}[]
  get_timetable_constraints: {[key: string]: any}[]
  get_generated_timetable: {[key: string]: any}[]
  // local ticks and counters
  constraintsUpdated: number
  selectedBranchId: number | string | null
  selectedLevelId: number | string | null
  selectedDurationId: number | string | null
  selectedVersionId: number | string | null
  dashboard_filters: {
    hello: boolean
    user_status: boolean
    user_top_links: boolean
    activity: boolean
    attendance: boolean
    accounting: boolean
    fee: boolean
    news: boolean
    schedule: boolean
    weeklySchedule: boolean
  }
  // Chatbot state
  chatbot_messages: {[key: string]: any}[]
  chatbot_conversation_history: {query: string; answer: string}[]
  chatbot_is_typing: boolean
  chatbot_menu_state: {
    isMenuOpen: boolean
    isSubmenuOpen: boolean
    isConversationStarted: boolean
  }
  chatbot_selected_menu_path: (string | number)[]
}

const initialState: MainReducer = {
  selectedLanguage: '',
  isFetching: {},
  subjects: [],
  headerDropdown: false,
  generalSettings: {},
  generalSettingsLoaded: false,
  userPreferences: null,
  branches: [],
  userBranches: [],
  custom_titles: [],
  tenantSessions: {},
  classes: [],
  grades: [],
  teachers: [],
  students: [],
  sections: [],
  timetable_list_stats: null,
  attendance_setting: [],
  get_user_subjects: [],
  levels: [],
  get_timetable_categories: [],
  get_timetable_subcategories: [],
  get_timetable_constraints: [],
  get_generated_timetable: [],
  // local ticks and counters
  constraintsUpdated: 0,
  selectedBranchId: null,
  selectedLevelId: null,
  selectedDurationId: null,
  selectedVersionId: null,
  // Chatbot state
  chatbot_messages: [],
  chatbot_conversation_history: [],
  chatbot_is_typing: false,
  chatbot_menu_state: {
    isMenuOpen: true,
    isSubmenuOpen: false,
    isConversationStarted: false,
  },
  chatbot_selected_menu_path: [], // Array of menu item IDs representing navigation path
  dashboard_filters: {
    hello: true,
    user_status: true,
    user_top_links: true,
    activity: true,
    attendance: true,
    accounting: false,
    fee: true,
    news: true,
    schedule: true,
    weeklySchedule: true,
  },
}

const subjectColors = [
  '#d50000',
  '#c51162',
  '#aa00ff',
  '#6200ea',
  '#304ffe',
  '#2962ff',
  '#0091ea',
  '#00b8d4',
  '#00bfa5',
  '#00c853',
  '#64dd17',
  '#aeea00',
  '#ffd600',
  '#ffab00',
  '#ff6d00',
  '#dd2c00',
  '#3e2723',
  '#212121',
  '#455a64',
]

const reducer = (state = initialState, action: any) => {
  const safeChatbotMessages = Array.isArray(state && state.chatbot_messages)
    ? state.chatbot_messages
    : []

  switch (action.type) {
    case REDUX_SELECT_LANGUAGE:
      return {
        ...state,
        selectedLanguage: action.payload.data,
      }
    case REDUX_STATE_LOADING: {
      const {data} = action.payload
      return {
        ...state,
        isFetching: {...state.isFetching, ...data},
      }
    }
    case REDUX_SAVE_SUBJECTS: {
      const {result} = action.payload
      result.map((subject: any, i: number) => {
        return (subject.color = subjectColors[i]
          ? subjectColors[i]
          : ('#' + ((Math.random() * 0xffffff) << 0).toString(16) + '000000').slice(0, 7))
      })
      return {
        ...state,
        subjects: result,
      }
    }
    case REDUX_SET_HEADER_DROPDOWN:
      return {
        ...state,
        headerDropdown: action.payload.data,
      }
    case REDUX_GENERAL_SETTINGS: {
      const rawTabTitle = action?.payload?.result?.tabTitle
      const tabTitle = typeof rawTabTitle === 'string' ? rawTabTitle.trim() : ''
      document.title = isSelectiveAiDomains() ? 'Da1Timetable' : tabTitle || 'Ilmversity'

      return {
        ...state,
        generalSettings: action.payload.result,
        generalSettingsLoaded: true,
      }
    }
    case REDUX_USER_PREFERENCES:
      return {
        ...state,
        userPreferences: action.payload?.result ?? {},
      }
    case REDUX_SET_BRANCHES: {
      const branches = action.payload.result

      // * userBranches are set in the payload in fetchData saga helper as we can not access userReducer here which is need for filtering branches
      const userBranches = action.payload.userBranches

      return {
        ...state,
        branches,
        userBranches,
      }
    }
    case REDUX_SET_CUSTOM_TITLES:
      return {
        ...state,
        custom_titles: action.payload.result,
      }
    case REDUX_TENANT_SESSIONS:
      return {
        ...state,
        tenantSessions: action.payload.result,
      }
    case REDUX_GET_CLASSES:
      return {
        ...state,
        classes: action.payload.result?.data || [],
      }

    case REDUX_GET_ATTENDANCE_SETTING:
      return {
        ...state,
        attendance_setting: action.payload.result?.data,
      }

    case REDUX_UPDATE_ATTENDANCE_SETTING: {
      const {level_ids, updatedFields} = action.payload

      const updatedSettings = state.attendance_setting.map((setting: any) => {
        if (level_ids.includes(setting.levelId)) {
          return {
            ...setting,
            ...updatedFields,
          }
        }
        return setting
      })

      return {
        ...state,
        attendance_setting: updatedSettings,
      }
    }

    case REDUX_GET_USER_SUBJECTS:
      return {
        ...state,
        get_user_subjects: action.payload.result.data,
      }
    case REDUX_LEVELS:
      return {
        ...state,
        levels: action.payload.result.data,
      }
    case REDUX_GET_GRADES:
      return {
        ...state,
        grades: action.payload.result?.data,
      }
    case REDUX_GET_TEACHERS:
      return {
        ...state,
        teachers: action.payload.result?.data || [],
      }
    case REDUX_GET_STUDENTS:
      return {
        ...state,
        students: action.payload.result?.data || [],
      }
    case REDUX_GET_SECTIONS:
      return {
        ...state,
        sections: action.payload.result?.data || [],
      }
    case REDUX_GET_TIMETABLE_LIST_DATA:
      return {
        ...state,
        timetable_list_stats: action.payload.result?.[0] ?? null,
      }
    case REDUX_GET_TIMETABLE_CATEGORIES:
      return {
        ...state,
        get_timetable_categories: action.payload.result?.data,
      }
    case REDUX_GET_TIMETABLE_SUBCATEGORY_BY_ID:
      return {
        ...state,
        get_timetable_subcategories: action.payload.result?.data,
      }
    case REDUX_GET_ALL_CONSTRAINTS:
      return {
        ...state,
        get_timetable_constraints: action.payload.result?.data,
      }
    case REDUX_GET_TIMETABLE:
      return {
        ...state,
        get_generated_timetable: action.payload.result?.data,
      }
    case 'REDUX_INCREMENT_CATEGORY_PREFERENCE_COUNT': {
      const {categoryId, delta = 1} = action.payload || {}
      const categories = Array.isArray(state.get_timetable_categories)
        ? state.get_timetable_categories.map((c: any) =>
            Number(c.id) === Number(categoryId)
              ? {...c, preference_count: Number(c.preference_count || 0) + Number(delta)}
              : c
          )
        : state.get_timetable_categories
      return {
        ...state,
        get_timetable_categories: categories,
      }
    }
    case 'REDUX_DECREMENT_CATEGORY_PREFERENCE_COUNT': {
      const {categoryId, delta = 1} = action.payload || {}
      const categories = Array.isArray(state.get_timetable_categories)
        ? state.get_timetable_categories.map((c: any) =>
            Number(c.id) === Number(categoryId)
              ? {
                  ...c,
                  preference_count: Math.max(0, Number(c.preference_count || 0) - Number(delta)),
                }
              : c
          )
        : state.get_timetable_categories
      return {
        ...state,
        get_timetable_categories: categories,
      }
    }
    case 'REDUX_INCREMENT_SUBCATEGORY_PREFERENCE_COUNT': {
      const {subCategoryId, delta = 1} = action.payload || {}
      const subcategories = Array.isArray(state.get_timetable_subcategories)
        ? state.get_timetable_subcategories.map((s: any) =>
            Number(s.id) === Number(subCategoryId)
              ? {...s, preference_count: Number(s.preference_count || 0) + Number(delta)}
              : s
          )
        : state.get_timetable_subcategories
      return {
        ...state,
        get_timetable_subcategories: subcategories,
      }
    }
    case 'REDUX_DECREMENT_SUBCATEGORY_PREFERENCE_COUNT': {
      const {subCategoryId, delta = 1} = action.payload || {}
      const subcategories = Array.isArray(state.get_timetable_subcategories)
        ? state.get_timetable_subcategories.map((s: any) =>
            Number(s.id) === Number(subCategoryId)
              ? {
                  ...s,
                  preference_count: Math.max(0, Number(s.preference_count || 0) - Number(delta)),
                }
              : s
          )
        : state.get_timetable_subcategories
      return {
        ...state,
        get_timetable_subcategories: subcategories,
      }
    }
    case 'REDUX_CONSTRAINTS_UPDATED_TICK': {
      return {
        ...state,
        constraintsUpdated: Number(state.constraintsUpdated || 0) + 1,
      }
    }
    case REDUX_SET_SELECTED_BRANCH_ID:
      return {
        ...state,
        selectedBranchId: action.payload,
      }
    case REDUX_SET_SELECTED_LEVEL_ID:
      return {
        ...state,
        selectedLevelId: action.payload,
      }
    case REDUX_SET_SELECTED_DURATION_ID:
      return {
        ...state,
        selectedDurationId: action.payload,
      }
    case REDUX_SET_SELECTED_VERSION_ID:
      return {
        ...state,
        selectedVersionId: action.payload,
      }
    case 'SET_DASHBOARD_FILTERS':
      return {
        ...state,
        dashboard_filters: {
          ...state.dashboard_filters,
          [action.payload.key]: action.payload.value,
        },
      }
    case REDUX_ADD_USER_MESSAGE:
      return {
        ...state,
        chatbot_messages: [...safeChatbotMessages, action.payload.message],
      }
    case REDUX_ADD_BOT_MESSAGE:
      return {
        ...state,
        chatbot_messages: [...safeChatbotMessages, action.payload.message],
        chatbot_is_typing: false,
      }
    case REDUX_SET_TYPING:
      return {
        ...state,
        chatbot_is_typing: action.payload.isTyping,
      }
    case REDUX_CLEAR_CHAT:
      return {
        ...state,
        chatbot_messages: [],
        chatbot_conversation_history: [],
        chatbot_is_typing: false,
        chatbot_menu_state: {
          isMenuOpen: true,
          isSubmenuOpen: false,
          isConversationStarted: false,
        },
        chatbot_selected_menu_path: [],
      }
    case REDUX_UPDATE_MESSAGE_STATUS:
      return {
        ...state,
        chatbot_messages: safeChatbotMessages.map((msg) =>
          msg.id === action.payload.messageId ? {...msg, status: action.payload.status} : msg
        ),
      }

    case REDUX_SET_MESSAGE_UNDERSTOOD_AS:
      return {
        ...state,
        chatbot_messages: safeChatbotMessages.map((msg) =>
          msg.id === action.payload.messageId
            ? {...msg, understoodAs: action.payload.understoodAs}
            : msg
        ),
      }
    case 'REDUX_REMOVE_CHAT_MESSAGE':
      return {
        ...state,
        chatbot_messages: safeChatbotMessages.filter((msg) => msg.id !== action.payload.messageId),
      }
    case REDUX_ADD_CONVERSATION_HISTORY:
      return {
        ...state,
        chatbot_conversation_history: [
          ...state.chatbot_conversation_history,
          {query: action.payload.query, answer: action.payload.answer},
        ],
      }
    case REDUX_SET_MENU_STATE:
      return {
        ...state,
        chatbot_menu_state: {
          ...state.chatbot_menu_state,
          ...action.payload,
        },
      }
    case REDUX_RESET_MENU_STATE:
      return {
        ...state,
        chatbot_menu_state: {
          isMenuOpen: true,
          isSubmenuOpen: false,
          isConversationStarted: false,
        },
        chatbot_selected_menu_path: [],
      }
    case REDUX_SET_SELECTED_MENU_PATH:
      return {
        ...state,
        chatbot_selected_menu_path: action.payload,
      }
    case REDUX_RESTORE_CHAT_SESSION:
      return {
        ...state,
        chatbot_messages: Array.isArray(action.payload.messages)
          ? action.payload.messages.map((m: {timestamp: string | number | Date}) => ({
              ...m,
              timestamp: m && m.timestamp ? new Date(m.timestamp) : new Date(),
            }))
          : [],
        chatbot_conversation_history: Array.isArray(action.payload.conversationHistory)
          ? action.payload.conversationHistory
          : [],
        chatbot_menu_state: action.payload.menuState || {
          isMenuOpen: true,
          isSubmenuOpen: false,
          isConversationStarted: false,
        },
        chatbot_selected_menu_path: action.payload.selectedMenuPath || [],
        chatbot_is_typing: false,
      }
    case REDUX_DESTROY_SESSION:
      return {
        ...state,
        userPreferences: null,
      }
    default:
      return state
  }
}

export default reducer
